import { useMemo } from 'react'

import { Product } from '../utils/types/Product'
import useLocalStorageViewedProducts from '../components/hooks/useLocalStorageViewedProducts'
import { useApi } from './useApi'
import { buildProductCodesQuery } from '../utils/api'
import { AnalyticsProductCarouselTypes } from '../utils/types/analytics'

interface UseRecentlyViewedProductsOptions {
  limit?: number
}

interface UseRecentlyViewedProductsResult {
  data: Product[] | undefined
  isLoading: boolean
  error: any
  carouselType: typeof AnalyticsProductCarouselTypes.RecentlyViewed
}

/**
 * Hook to fetch and return recently viewed products
 *
 * Uses local storage to track product SKUs and fetches the full product data
 *
 * @param options Configuration options
 * @returns Recently viewed products in order of most recently viewed first
 */
const useRecentlyViewedProducts = (
  options: UseRecentlyViewedProductsOptions = {},
): UseRecentlyViewedProductsResult => {
  const { limit = 15 } = options
  const { viewedSkus } = useLocalStorageViewedProducts()
  const { getResource } = useApi()

  // Fetch minimum of last 15 viewed SKUs unless limit parameter exceeds 15.
  // This ensures that even if latest viewed products aren't available in the current country,
  // the response will return the next most recently viewed products for the current country as
  // long as they're available in the first 15 products.
  // When limit exceeds 15, the API will fetch limit amount of products.
  const productCodes = useMemo(() => [...viewedSkus] // Create copy to avoid mutating original
    .reverse()
    .slice(0, limit < 15 ? 15 : limit),
  [viewedSkus, limit])

  // Build query to fetch products based on SKUs
  const query = useMemo(
    () => buildProductCodesQuery(productCodes),
    [productCodes],
  )

  const {
    data: fetchedProducts,
    isLoading,
    error,
  } = getResource<Product>('product', query || undefined)

  // Sort products to match the order of SKUs in local storage
  const products = useMemo(() => {
    if (!fetchedProducts || !productCodes.length) return fetchedProducts

    // Create a map of product code to product for quick lookup
    const productMap = fetchedProducts.reduce<Record<string, Product>>((map, product) => {
      if (product['product.code']) {
        return { ...map, [product['product.code']]: product }
      }
      return map
    }, {})

    // Return products in the same order as the product codes
    return productCodes
      .map((code) => productMap[code])
      .filter(Boolean) // Remove undefined products
  }, [fetchedProducts, productCodes])

  const areValidProducts = products.every((product) => product.media)

  return {
    data: areValidProducts ? products.slice(0, limit) : undefined,
    isLoading,
    error,
    carouselType: AnalyticsProductCarouselTypes.RecentlyViewed,
  }
}

export default useRecentlyViewedProducts
